import React from 'react';
import { createPortal } from 'react-dom';
import { getEstadoChipClass, getMotivoChipClass, getPersonaChipClass } from '../../utils/styles'; 

const ModalDetallePoliza = ({ show, onClose, poliza }) => {

  if (!show || !poliza) return null;

  // Lógica de Caución Especial para avisos técnicos
  const caucionEspecialList = ['ANTICIPO FINANCIERO', 'EJECUCIÓN DE CONTRATO', 'SUSTITUCIÓN DE FONDO DE REPARO'];
  const esCaucionEspecial = poliza.riesgo?.toUpperCase() === 'CAUCIÓN' && caucionEspecialList.includes(poliza.subriesgo?.toUpperCase());
  
  // Validación de póliza que proviene de renovación previa
  const esPolizaRenovada = Boolean(
    poliza.polizaExpirante || 
    poliza.companiaExpirante || 
    poliza.ciaExpirante || 
    poliza.quienRenovo
  );

  const esNoRenueva = poliza.estadoGestionRenovacion === 'NO RENUEVA';

  // Helper para formatear fechas
  const formatDate = (dateString) => {
    if (!dateString) return null;
    if (dateString.seconds) return new Date(dateString.seconds * 1000).toLocaleDateString('es-AR');
    
    if (typeof dateString === 'string' && dateString.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
            const [y, m, d] = parts;
            return `${d}/${m}/${y}`;
        }
    }
    return dateString;
  };

  // Helper para formatear moneda
  const formatMoney = (val) => {
      if (!val && val !== 0) return '0,00';
      let num = val;
      if (typeof val === 'string') {
           const cleanVal = val.replace(/[^0-9,.-]/g, '');
           num = parseFloat(cleanVal.replace(/\./g, '').replace(',', '.'));
      }
      if (isNaN(num)) return '0,00';
      return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };

  const DetailItem = ({ label, value, highlight = false }) => (
    <div className="text-left">
      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 text-left">{label}</p>
      <div className={`text-base font-medium break-words min-h-[1.5rem] text-left ${highlight ? 'text-indigo-700 font-bold' : 'text-gray-800'}`}>
        {value || <span className="text-gray-300 italic font-normal text-sm text-left">Sin dato</span>}
      </div>
    </div>
  );

  const CurrencyItem = ({ label, currency, amount, isNegative, colorClass = "text-blue-600" }) => (
    <div className="text-left">
      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 text-left">{label}</p>
      <div className="text-base font-medium text-gray-800 break-words min-h-[1.5rem] text-left">
        {(amount || amount === 0) ? (
            <span>
                <span className={`font-bold mr-1.5 ${isNegative ? 'text-red-600' : colorClass}`}>
                    {currency || '$'} {isNegative ? '-' : ''}
                </span>
                {formatMoney(amount)}
            </span>
        ) : (
            <span className="text-gray-300 italic text-sm text-left">Sin dato</span>
        )}
      </div>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left font-['Inter']">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-auto max-h-[95vh] flex flex-col overflow-hidden animate-fade-in border border-gray-100 text-left">
        
        {/* Header */}
        <div className="flex justify-between items-center px-8 py-5 border-b border-gray-100 shrink-0 bg-white text-left">
            <div className="text-left">
                <h3 className="text-2xl font-bold text-gray-800 text-left">Detalle de Póliza</h3>
                <div className="mt-2 flex items-center gap-3 text-left">
                    <span className="bg-gray-100 px-3 py-1 rounded-md border border-gray-200 font-mono font-bold text-gray-800 text-lg text-left">
                        #{poliza.nPoliza || 'Pendiente'}
                    </span>
                    <span className={`font-bold px-3 py-1 rounded-md text-sm uppercase border ${getEstadoChipClass(poliza.estadoEmision)}`}>
                        {poliza.estadoEmision}
                    </span>
                </div>
            </div>
            <div className="flex items-center gap-3">
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-50">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>
            </div>
        </div>

        {/* Content Scrollable */}
        <div className="overflow-y-auto p-8 flex-grow bg-white space-y-8 text-left custom-scrollbar">

            {/* SECCIÓN 0: DATOS DE ANULACIÓN */}
            {(poliza.estadoEmision === 'ANULADA' || poliza.estadoEmision === 'Anulada') && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-6 relative text-left">
                     <legend className="px-3 py-1 text-sm font-bold text-red-700 bg-red-100 rounded-full border border-red-200 absolute -top-3 left-4 flex items-center shadow-sm text-left uppercase">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                        Datos de Anulación
                     </legend>
                     <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-2 text-left">
                        <DetailItem label="Motivo Anulación" value={poliza.motivoAnulacion} />
                        <DetailItem label="Fecha Anulación" value={formatDate(poliza.fechaAnulacion)} />
                        <CurrencyItem label="Prima Anulación" currency={poliza.moneda} amount={poliza.primaAnulacion} isNegative={true} />
                        <DetailItem label="¿Quién Gestionó?" value={poliza.quienGestiono} />
                        
                        <div className="md:col-span-4 mt-2">
                            <p className="text-xs font-bold text-red-400 uppercase tracking-wider mb-2">Observaciones de la Anulación</p>
                            <div className="bg-white/60 p-4 rounded-xl border border-red-100 min-h-[4rem]">
                                <p className="text-sm text-red-900 whitespace-pre-wrap italic">
                                    {poliza.observacionesAnulacion || "Sin observaciones detalladas."}
                                </p>
                            </div>
                        </div>
                     </div>
                </div>
            )}

            {/* 1. DETALLES DEL RIESGO */}
            <fieldset className="border border-gray-200 rounded-xl p-6 relative mt-2 text-left">
                <legend className="px-2 text-lg font-bold text-gray-700 bg-white absolute -top-3 left-4">Detalles del Riesgo</legend>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-2 text-left">
                    <DetailItem label="Fecha de Carga" value={formatDate(poliza.fechaCarga)} />
                    <DetailItem label="N° de Póliza" value={poliza.nPoliza} highlight />
                    
                    <div className="text-left">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 text-left">Tipo de Operación</p>
                        <div className="mt-1 text-left">
                            {poliza.motivo ? (
                                <span className={`px-3 py-1 rounded-full text-sm font-medium uppercase border ${getMotivoChipClass(poliza.motivo)}`}>
                                    {poliza.motivo}
                                </span>
                            ) : <span className="text-gray-300 italic text-sm text-left">Sin dato</span>}
                        </div>
                    </div>

                    <DetailItem label="Sub-Operación" value={poliza.subOperacion} />        
                    
                    <DetailItem label="Vigencia Desde" value={formatDate(poliza.inicioVigencia)} />
                    <DetailItem label="Vigencia Hasta" value={formatDate(poliza.finVigencia)} />
                    <DetailItem label="Riesgo" value={poliza.riesgo} />
                    <DetailItem label="Subriesgo" value={poliza.subriesgo} />

                    <div className="hidden md:block"></div> 

                    {/* CAMPOS DINÁMICOS */}
                    {poliza.patentes?.filter(p => p !== '').length > 0 && (
                        <div className="md:col-span-4 text-left">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 text-left">Patentes</p>
                            <div className="flex flex-wrap gap-1 mt-1 text-left">
                                {poliza.patentes.filter(p => p !== '').map((p, i) => (
                                    <span key={i} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-sm font-medium border border-gray-200 text-left">{p}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {poliza.actividades?.filter(a => a !== '').length > 0 && (
                        <div className="md:col-span-2 text-left">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 text-left">Actividades</p>
                            <div className="flex flex-wrap gap-1 mt-1 text-left">
                                {poliza.actividades.filter(a => a !== '').map((a, i) => (
                                    <span key={i} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-sm font-medium border border-blue-200 text-left">{a}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {poliza.especialidades?.filter(e => e !== '').length > 0 && (
                        <div className="md:col-span-2 text-left">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 text-left">Especialidades</p>
                            <div className="flex flex-wrap gap-1 mt-1 text-left">
                                {poliza.especialidades.filter(e => e !== '').map((e, i) => (
                                    <span key={i} className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded text-sm font-medium border border-indigo-200 text-left">{e}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {poliza.ubicacionesRiesgo?.filter(u => u !== '' && (typeof u === 'string' || (u.direccion && u.direccion.trim() !== ''))).length > 0 && (
                        <div className="md:col-span-3 bg-gray-50 p-3 rounded-lg border border-gray-100 mt-2 text-left">
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 text-left">Ubicaciones del Riesgo</p>
                            <ul className="list-disc list-inside text-sm text-gray-700 mt-1 text-left">
                                {poliza.ubicacionesRiesgo.map((u, i) => {
                                    if (typeof u === 'string' && u.trim() !== '') {
                                        return <li key={i}>{u}</li>;
                                    } else if (typeof u === 'object' && u.direccion?.trim() !== '') {
                                        return (
                                            <li key={i}>
                                                {u.direccion} 
                                                {u.m2 ? <span className="font-semibold text-gray-500 ml-1">({u.m2} m²)</span> : ''}
                                            </li>
                                        );
                                    }
                                    return null;
                                })}
                            </ul>
                        </div>
                    )}

                    {/* INDICADORES DE PÓLIZA */}
                    <div className="md:col-span-4 mt-4 pt-4 border-t border-gray-50 text-left">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 text-left">Indicadores Técnicos</p>
                        <div className="flex flex-wrap gap-3">
                            {poliza.isPolizaPack && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-orange-50 text-orange-800 border border-orange-200 shadow-sm uppercase tracking-tighter gap-1.5">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                                    Vínculo Pack: {poliza.referenciasPolizaPack?.join(", ") || 'S/D'}
                                </span>
                            )}

                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border shadow-sm uppercase tracking-tighter gap-1.5 ${
                                poliza.formaRenovacion === 'AUTOMÁTICA' 
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                                : 'bg-orange-50 text-orange-600 border-orange-200'
                            }`}>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.678 48.678 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3l-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3l-3 3" /></svg>
                                Renovación: {poliza.formaRenovacion || 'MANUAL'}
                            </span>

                            {poliza.tipoRenovacion && (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 shadow-sm uppercase tracking-tighter gap-1.5">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>
                                    Tipo: {poliza.tipoRenovacion}
                                </span>
                            )}
                        </div>
                        {esCaucionEspecial && <p className="mt-4 font-bold text-orange-500 uppercase italic text-xs">Póliza de Caución con Vigencia Extendida / Refacturable</p>}
                    </div>
                </div>
            </fieldset>

            {/* SECCIÓN NUEVA: ORIGEN Y ANTECEDENTES DE RENOVACIÓN (SÓLO SI VIENE DE OTRA PÓLIZA) */}
            {esPolizaRenovada && (
                <fieldset className="border border-slate-200 rounded-xl p-6 relative mt-4 bg-slate-50/50 text-left">
                    <legend className="px-2 text-lg font-bold text-slate-700 bg-white rounded-lg absolute -top-3 left-4">
                        Origen y Antecedente de Renovación
                    </legend>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 text-left">
                        <DetailItem label="Quién Renovó" value={poliza.quienRenovo} />
                        <DetailItem label="Póliza Expirante" value={poliza.polizaExpirante} highlight />
                        <DetailItem label="Compañía Expirante" value={poliza.companiaExpirante || poliza.ciaExpirante} />
                    </div>
                </fieldset>
            )}

            {/* 2. DATOS DE LA COMPAÑÍA */}
            <fieldset className="border border-gray-200 rounded-xl p-6 relative mt-4 text-left">
                <legend className="px-2 text-lg font-bold text-gray-700 bg-white absolute -top-3 left-4">Datos de la Compañía</legend>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 text-left">
                    <DetailItem label="Compañía" value={poliza.compania} highlight />
                    <DetailItem label="Organizador" value={poliza.organizador} />
                    <DetailItem label="PAS" value={poliza.pas} />
                </div>
            </fieldset>

            {/* 3. EQUIPO COMERCIAL */}
            <fieldset className="border border-gray-200 rounded-xl p-6 relative mt-4 text-left">
                <legend className="px-2 text-lg font-bold text-gray-700 bg-white absolute -top-3 left-4">Equipo Comercial</legend>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 text-left">
                    <DetailItem label="Tipo de Canal" value={poliza.canal} />
                    <DetailItem label="Nombre del Canal" value={poliza.canalNombre || poliza.subcanal} />
                    <DetailItem label="Subcanal Asociado" value={poliza.subcanalNombre} />
                    
                    <div className="col-span-1 md:col-span-3 border-t border-dashed border-indigo-100 my-2 text-left"></div>

                    <div className="text-left">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 text-left">Vendedor</p>
                        <div className="mt-1 text-left">
                             {poliza.vendedor ? <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${getPersonaChipClass(poliza.vendedor)}`}>{poliza.vendedor}</span> : '-'}
                        </div>
                    </div>

                    <div className="text-left">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 text-left">Suscriptor</p>
                        <div className="mt-1 text-left">
                             {poliza.suscriptor ? <span className={`px-2 py-0.5 rounded-full text-sm font-medium ${getPersonaChipClass(poliza.suscriptor)}`}>{poliza.suscriptor}</span> : '-'}
                        </div>
                    </div>

                    <DetailItem label="Tipo Conversión" value={poliza.tipoConversion} />
                    <DetailItem label="Contador Asociado" value={poliza.contadorNombre} />
                </div>
            </fieldset>

            {/* 4. FACTURACIÓN Y VALORES */}
            <fieldset className="border border-indigo-200 rounded-xl p-6 relative mt-4 bg-indigo-50/20 text-left">
                <legend className="px-2 text-lg font-bold text-indigo-700 bg-white rounded-lg absolute -top-3 left-4 tracking-wide">
                    Facturación y Valores
                </legend>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-2 text-left">
                    <DetailItem label="Forma de Pago" value={poliza.formaPago} />
                    <DetailItem label="Tipo Facturación" value={poliza.tipoFacturacion} />
                    <DetailItem label="Cuotas" value={poliza.cuotas ? `${poliza.cuotas} Cuotas` : 'S/D'} />
                    <DetailItem label="Medio de Pago" value={poliza.medioPago} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-6 mt-4 border-t border-indigo-100 text-left">
                    <CurrencyItem label="Prima Emitida" currency={poliza.moneda} amount={poliza.primaEmitida} />
                    <CurrencyItem label="Premio" currency={poliza.moneda} amount={poliza.premio} />
                    
                    {poliza.primaAdelanto > 0 && (
                        <CurrencyItem label="Prima Adelanto" currency={poliza.moneda} amount={poliza.primaAdelanto} colorClass="text-emerald-600" />
                    )}
                    {poliza.primaPrimeraCuota > 0 && (
                        <CurrencyItem label="Prima Primera Cuota" currency={poliza.moneda} amount={poliza.primaPrimeraCuota} colorClass="text-indigo-600" />
                    )}
                    
                    <DetailItem label="% Comisión Total" value={poliza.comision ? `${poliza.comision}%` : '0%'} />
                </div>
            </fieldset>

            {/* 5. DESPACHO E INFO ADICIONAL */}
            <fieldset className="border border-gray-200 rounded-xl p-6 relative mt-4 text-left">
                <legend className="px-2 text-lg font-bold text-gray-700 bg-white absolute -top-3 left-4">Despacho e Info Adicional</legend>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-2 text-left">
                    <DetailItem label="Fecha Despacho" value={formatDate(poliza.fechaDespacho)} />
                    <DetailItem label="Forma Despacho" value={poliza.formaDespacho} />
                    <DetailItem label="Próx. Refacturación" value={formatDate(poliza.fechaRefacturacion)} />
                    <DetailItem label="Post Venta" value={poliza.postventa} />
                    
                    <div className="md:col-span-3 mt-4 text-left">
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 text-left">Observaciones Internas</p>
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 min-h-[4rem] text-left">
                            <p className="text-sm text-gray-700 whitespace-pre-wrap text-left">
                                {poliza.observaciones || <span className="text-gray-300 italic">Sin observaciones.</span>}
                            </p>
                        </div>
                    </div>
                </div>
            </fieldset>

            {/* 6. GESTIÓN DE RENOVACIÓN (ACTIVA) */}
            <fieldset className="border border-orange-200 rounded-xl p-6 relative mt-4 bg-orange-50/10 text-left">
                <legend className="px-2 text-lg font-bold text-orange-700 bg-white rounded-lg absolute -top-3 left-4">
                    Gestión de Renovación
                </legend>

                <div className="space-y-4 pt-2 text-left">
                    {/* Grid adaptativo: 3 columnas si es NO RENUEVA para colocar el motivo al lado */}
                    <div className={`grid grid-cols-1 ${esNoRenueva ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
                        <DetailItem label="Estado de gestión" value={poliza.estadoGestionRenovacion} />

                        {/* CAMPO DINÁMICO: Visible únicamente si el estado es NO RENUEVA */}
                        {esNoRenueva && (
                            <div className="animate-fade-in">
                                <DetailItem 
                                    label="Motivo No Renueva" 
                                    value={
                                        poliza.motivoNoRenueva ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                                                {poliza.motivoNoRenueva}
                                            </span>
                                        ) : null
                                    } 
                                />
                            </div>
                        )}

                        <DetailItem label="Responsable de la Gestión" value={poliza.gestionadoPor} />

                        {/* Observaciones a ancho completo */}
                        <div className={esNoRenueva ? 'md:col-span-3' : 'md:col-span-2'}>
                            <p className="text-xs font-bold text-orange-400 uppercase tracking-wider mb-2 text-left">
                                Observaciones de Renovación
                            </p>
                            <div className="bg-white/60 p-4 rounded-xl border border-orange-100 min-h-[4rem] text-left">
                                <p className="text-sm text-orange-900 whitespace-pre-wrap italic">
                                    {poliza.observacionesRenovacion || "No se registraron comentarios en la gestión de renovación."}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>

        </div>
        
        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-100 flex justify-end bg-gray-50 shrink-0 text-left">
            <button onClick={onClose} className="px-8 py-2.5 rounded-full bg-white border border-gray-300 text-gray-700 font-bold hover:bg-gray-100 transition shadow-sm text-left uppercase text-xs tracking-widest active:scale-95">
                Cerrar
            </button>
        </div>

      </div>
      <style>{`.animate-fade-in { animation: fadeIn 0.3s ease-out; } @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } } .custom-scrollbar::-webkit-scrollbar { width: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }`}</style>
    </div>,
    document.body
  );
};

export default ModalDetallePoliza;